"use client";

import Image from "@components/atoms/Image";
import Button from "@components/atoms/Button";
import Text from "@components/atoms/Text";
import styles from "./ChatbotSupport.module.scss";
import assetLoader from "@utils/internalImageLoder";
import { trackEventWithCommonProps, EventType } from "@utils/eventTracker";
import { useAppDispatch } from "@lib/redux/hooks";
import { setCopilotCarData } from "@lib/redux/slices/carDataSlice";

interface ChatbotSupportProps {
  heading: string;
  subheading?: string;
  buttonText: string;
  buttonLink?: string;
  icon: string;
  customChatbotStyling?: string;
  buttonStyle?: string;
  chatBotPrompt?: string;
  brandName?: string;
  modelName?: string;
  variantName?: string;
  cityName?: string;
  carAvailability?: string;
  section?: string;
  pageType?: string;
}

export default function ChatbotSupport({
  heading,
  subheading,
  buttonText,
  icon,
  customChatbotStyling,
  buttonStyle,
  chatBotPrompt,
  brandName,
  modelName,
  variantName,
  cityName,
  carAvailability,
  pageType,
  section,
}: ChatbotSupportProps) {

  const dispatch = useAppDispatch();
  
  const handleButtonClick = async () => {
    try {
      if (chatBotPrompt) {
        if ((window as any).copilot) {
          dispatch(setCopilotCarData({
            make: brandName || "",
            model: modelName || "",
            variant: variantName || "",
          }));

          (window as any).copilot.open();
          (window as any).copilot.sendMessage(chatBotPrompt);
        } else {
          // Track chatbot loading error
          await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
            pageType: pageType,
            additionalProps: {
              action_type: "system_error",
              action_name: "chat_bot_not_loading",
              section: section || "",
              car_availability: carAvailability,
              city_name: cityName,
              vehicle_make: brandName,
              vehicle_model: modelName,
              ...(variantName && { vehicle_variant: variantName }),
            },
          });
          console.error("Chatbot not available");
        }
      }
    } catch (error) {
      // Track chatbot loading error
      await trackEventWithCommonProps("exception", EventType.TRACK_EVENT, {
        pageType: pageType,
        additionalProps: {
          action_type: "system_error",
          action_name: "chat_bot_not_loading",
          section: section || "",
          car_availability: carAvailability,
          city_name: cityName,
          vehicle_make: brandName,
          vehicle_model: modelName,
          ...(variantName && { vehicle_variant: variantName }),
        },
      });
      console.error("Error loading chatbot:", error);
    }
  };

  return (
    <>
      <div className={`${styles.budgetMatch} ${customChatbotStyling}`}>
        <Image
          src={`${assetLoader()}${icon}`}
          alt="Chatbot support"
          width={56}
          height={56}
          loading="lazy"
          className={styles.logo}
        />
        <div className={styles.contentContainer}>
          <div className={styles.textContainer}>
            <Text level="h2" className={styles.heading}>
              {heading}
            </Text>
            {subheading && (
              <Text level="p" className={styles.subheading}>
                {subheading}
              </Text>
            )}
          </div>
          <Button
            size="ds"
            className={`${styles.button} ${buttonStyle}`}
            onButtonClick={handleButtonClick}
            segmentEventProps={{
              eventName: "action",
              pageType: pageType,
              eventProperties: {
                action_type: "tap_btn",
                action_name: "tap_btn_chat_bot",
                prompt: chatBotPrompt || "",
                section: section || "",
                car_availability: carAvailability,
                city_name: cityName,
                vehicle_make: brandName,
                vehicle_model: modelName,
                ...(variantName && { vehicle_variant: variantName }),
              },
            }}
          >
            {buttonText}
          </Button>
        </div>
      </div>
    </>
  );
}
